import set from 'lodash/set'
import { object, string, number, date, AnyObject, ValidationError } from 'yup'
import { stripPhoneNumber } from '@utils/strings'

export const validations = {
  object,
  string,
  email: () =>
    string()
      .email()
      .test({
        name: 'is-typo',
        message: 'Did you mean .com?',
        skipAbsent: true,
        test(value = '', ctx) {
          const typo = value.match(
            /\.(cmo|cno|coom|comm|con|coj|cok|col|cim|cpm|cpn|don|doj|dok|dol|dom|vom|von|voj|vok|vol|xon|xoj|xok|xol|xom)$/i
          )
          if (typo) {
            return ctx.createError({
              message: `Did you mean .com? Found ${typo[0]}`,
            })
          }
          return true
        },
      }),
  number,
  date,
  time: () =>
    object().shape({
      hour: number().required('Please select an hour'),
      minute: number().required('Please select the minute'),
      meridiem: string().required('Please select AM or PM').oneOf(['AM', 'PM']),
    }),
  phone: () =>
    string().test(
      'valid-phone',
      'Not a valid phone number',
      (value?: string) => {
        const stripped = stripPhoneNumber(value)
        const isAmerican = stripped.startsWith('1')
        return isAmerican ? stripped.length === 11 : stripped.length >= 6
      }
    ),
}

export function createValidator<
  V extends AnyObject,
  S extends AnyObject = { [K in keyof V]?: any },
>(_schema: S) {
  const schema = validations.object(_schema)

  return async (values: V) => {
    let errors: { [k in keyof S]?: string } | undefined 

    try {
      await schema.validate(values, { abortEarly: false })
    } catch (err) {
      if (err instanceof ValidationError) {
        errors = err.inner.reduce(
          (formErrors, innerError) => {
            if (innerError.path) {
              set(formErrors, innerError.path, innerError.message)
            }
            return formErrors
          },
          {} as { [k in keyof S]: string | undefined }
        )
      } else {
        throw err
      }
    }

    return errors
  }
}
